import { ExecutionFile } from "../interfaces/common/common.interface";
import { VARIABLE_FILES_TOTAL_MAX_SIZE } from "../constants/file-upload.constants";

// START-CodeLeap [24-Aug-2026]: [#18188866946] jsonData file-ref shape
// One entry in a jsonData files array. Existing (server) files send name + exist:true;
// new uploads send exist:false plus a `fileRef` naming the multipart part with their binary.
export interface VariableFileRef {
  name: string;
  exist?: boolean;
  fileRef?: string;
}
// /END-CodeLeap [24-Aug-2026]: [#18188866946]

// START-CodeLeap [02-Sep-2026]: [#18188866946] pre-send readability probe
// A File is a live handle, not a copy: the browser reads it from disk while the request is in
// flight. A file renamed, moved or deleted after it was attached fails that read mid-stream and
// arrives as a bare FETCH_ERROR with no status and no detail, which is indistinguishable from a
// dropped connection. Reading one byte up front turns that into a named, actionable failure.
//
// FileReader, not Blob.arrayBuffer: jsdom implements neither arrayBuffer nor text on Blob, and
// arrayBuffer needs Safari 14+, which is inside our browserslist. A missing API caught by a bare
// catch would look identical to a missing file and would block every upload.
const isFileReadable = (file: File): Promise<boolean> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(true);
    reader.onerror = () => resolve(false);
    reader.readAsArrayBuffer(file.slice(0, 1));
  });

// Names of the file parts in this FormData that can no longer be read. Reads FormData directly
// rather than ExecutionFile[] so it stays usable from the transport layer, and so any future
// upload path is covered without having to remember a guard.
export const findUnreadableFormDataFiles = async (body: FormData): Promise<string[]> => {
  const parts: File[] = [];
  body.forEach((value) => {
    if (value instanceof File) parts.push(value);
  });
  const results = await Promise.all(parts.map((part) => isFileReadable(part)));
  return parts.filter((_, i) => !results[i]).map((part) => part.name);
};
// /END-CodeLeap [02-Sep-2026]: [#18188866946]

// Use a WeakMap to store FormData instances. This makes the numbering unique across
// every call that shares a FormData, so callers can loop field-by-field without threading
// a counter and still never collide on a `file_N` key.
// This is used to prevent the case where append files functions are called multiple times on the same FormData, which would otherwise reset the counter and overwrite previous files.
// START-CodeLeap [14-Aug-2026]: [#18188866946 PR-2469 review fixes] per-FormData fileRef counter so multi-call callers never collide on file_N
const formDataCounters = new WeakMap<FormData, { next: number }>();

const counterFor = (formData: FormData): { next: number } => {
  let counter = formDataCounters.get(formData);
  if (!counter) {
    counter = { next: 1 };
    formDataCounters.set(formData, counter);
  }
  return counter;
};
// /END-CodeLeap [14-Aug-2026]: [#18188866946 PR-2469 review fixes]

// Build the files array for the jsonData part and append each NEW file's binary to
// the FormData as `file_1`, `file_2`, ... . Existing (url-bearing) files are listed
// by name only (kept); omitting one from `files` deletes it server-side.
// Numbering is shared per FormData automatically, so calling this once per field on the
// same FormData still yields unique refs. Pass an explicit `counter` only to override that.
export const appendVariableFilesToFormData = (
  formData: FormData,
  files: ExecutionFile[],
  counter: { next: number } = counterFor(formData),
): VariableFileRef[] =>
  files.flatMap((file): VariableFileRef[] => {
    if (file.url) {
      return [{ name: file.name, exist: true }];
    }
    // START-CodeLeap [14-Aug-2026]: [#18188866946 PR-2469 review fixes]
    // A new entry with no File attached has nothing to send. Skip it rather than appending an
    // empty part the backend would store as a 0-byte file under a live fileRef.
    if (!file.file) {
      return [];
    }
    // /END-CodeLeap [14-Aug-2026]: [#18188866946 PR-2469 review fixes]
    const fileRef = `file_${counter.next++}`;
    // START-CodeLeap [02-Sep-2026]: [#18188866946] File is a Blob, so it appends as the binary
    // part directly. No encode/decode hop, and the browser streams it rather than holding it.
    formData.append(fileRef, file.file, file.name);
    // /END-CodeLeap [02-Sep-2026]: [#18188866946]
    return [{ name: file.name, fileRef }];
  });

// START-CodeLeap [14-Aug-2026]: [#18188866946 PR-2469 review fixes]
// Sum the byte size of a list of execution files. Stored (url-bearing) files without a
// size count as 0. Callers reduce across rows / fields with this for the cross-field cap.
export const getExecutionFilesTotalSize = (files: ExecutionFile[]): number =>
  files.reduce((acc, file) => acc + (file.size ?? 0), 0);
// /END-CodeLeap [14-Aug-2026]: [#18188866946 PR-2469 review fixes]

// START-CodeLeap [27-Aug-2026]: [#18188866946 PR-2469 review fixes] cross-field 25 MB guard,
// shared so the flight-director and manual-instance surfaces don't each carry their own copy
// [02-Sep-2026]: [PR-2615 review fixes] `>=`, matching validateFiles in
// gptBlueVariableFileService.cfc, which rejects at `GTE` the cap. Exactly 25 MB is over the limit.
export const exceedsVariableFilesTotal = (fileLists: ExecutionFile[][]): boolean =>
  fileLists.reduce((acc, files) => acc + getExecutionFilesTotalSize(files), 0) >= VARIABLE_FILES_TOTAL_MAX_SIZE;
// /END-CodeLeap [27-Aug-2026]: [#18188866946 PR-2469 review fixes]

// Nested variant: rows each carry their own `files`. File parts stay unique request-wide
// via the shared per-FormData counter. Used by the flight payload where files live under
// flightVariables[].files.
export const appendNestedVariableFilesToFormData = <T extends { files?: ExecutionFile[] }>(
  formData: FormData,
  rows: T[],
): (Omit<T, "files"> & { files: VariableFileRef[] })[] =>
  rows.map((row) => ({
    ...row,
    files: appendVariableFilesToFormData(formData, row.files ?? []),
  }));
